import { useState, useCallback, useEffect, useMemo } from 'react';
import { useSearchParams } from 'react-router-dom';

export type SortOption =
  | 'best-value'
  | 'recent'
  | 'mint'
  | 'price-low-high'
  | 'price-high-low'
  | 'rarity-low-high'
  | 'rarity-high-low'
  | 'name-a-z'
  | 'name-z-a';

export type OwnershipFilter = 'all' | 'owned' | 'not-owned';

/**
 * Sort options shown only on collections whose NFTs have meaningfully variable
 * points. Prepended to the main list when `hasVariablePoints` is true. We suppress
 * Best Value for uniform-points collections because the ratio is the same for every
 * listed NFT — the sort would just be price-sorted with extra steps.
 */
export const pointsSortOptions: { value: SortOption; label: string }[] = [
  { value: 'best-value', label: 'Best Value (Pts/XRP)' },
];

export const sortOptions: { value: SortOption; label: string }[] = [
  { value: 'recent', label: 'Recently Listed' },
  { value: 'mint', label: 'Recently Minted' },
  { value: 'price-low-high', label: 'Price: Low to High' },
  { value: 'price-high-low', label: 'Price: High to Low' },
  { value: 'rarity-low-high', label: 'Rarity: Rare to Common' },
  { value: 'rarity-high-low', label: 'Rarity: Common to Rare' },
  { value: 'name-a-z', label: 'Name: A-Z' },
  { value: 'name-z-a', label: 'Name: Z-A' },
];

export function getAvailableSortOptions(hasVariablePoints: boolean): { value: SortOption; label: string }[] {
  return hasVariablePoints ? [...pointsSortOptions, ...sortOptions] : sortOptions;
}

export const ownershipOptions: { value: OwnershipFilter; label: string }[] = [
  { value: 'all', label: 'All NFTs' },
  { value: 'owned', label: 'My NFTs' },
  { value: 'not-owned', label: 'Not Owned' },
];

export function parseSortOption(option: SortOption): [string, 'asc' | 'desc'] {
  switch (option) {
    case 'best-value':
      return ['best-value', 'desc'];
    case 'recent':
      return ['recent', 'desc'];
    case 'price-low-high':
      return ['price', 'asc'];
    case 'price-high-low':
      return ['price', 'desc'];
    case 'rarity-low-high':
      return ['rarity', 'asc'];
    case 'rarity-high-low':
      return ['rarity', 'desc'];
    case 'mint':
      return ['mint', 'desc'];
    case 'name-a-z':
      return ['name', 'asc'];
    case 'name-z-a':
      return ['name', 'desc'];
    default:
      return ['recent', 'desc'];
  }
}

interface UseNFTFiltersOptions {
  defaultSort: SortOption;
  /** Whether to sync filter state to/from URL search params */
  syncURL?: boolean;
  /** Callback when filters are applied (e.g., trigger refetch) */
  onApply?: () => void;
}

export interface NFTFiltersState {
  // State values
  sortOption: SortOption;
  minPrice: string;
  maxPrice: string;
  minRarity: string;
  maxRarity: string;
  minPoints: string;
  maxPoints: string;
  searchQuery: string;
  selectedTraits: { [key: string]: string[] };
  expandedTraits: Set<string>;
  ownershipFilter: OwnershipFilter;

  // UI toggles
  showSortDropdown: boolean;
  showFilters: boolean;
  showOwnershipDropdown: boolean;

  // Setters
  setMinPrice: (val: string) => void;
  setMaxPrice: (val: string) => void;
  setMinRarity: (val: string) => void;
  setMaxRarity: (val: string) => void;
  setMinPoints: (val: string) => void;
  setMaxPoints: (val: string) => void;
  setSearchQuery: (val: string) => void;
  setOwnershipFilter: (val: OwnershipFilter) => void;
  setShowSortDropdown: (val: boolean) => void;
  setShowFilters: (val: boolean) => void;
  setShowOwnershipDropdown: (val: boolean) => void;

  // Handlers
  handleSortChange: (newSort: SortOption) => void;
  handleFilterApply: () => void;
  handleFilterClear: () => void;
  handleClearPriceFilter: () => void;
  handleClearRarityFilter: () => void;
  handleClearPointsFilter: () => void;
  handleTraitToggle: (traitType: string, value: string) => void;
  toggleTraitExpanded: (traitType: string) => void;
  getCurrentSortLabel: () => string;

  /** Build URLSearchParams for API calls (sort, price, rarity, points, search, traits) */
  buildApiParams: () => URLSearchParams;

  /** Whether any filters are active */
  hasActiveFilters: boolean;

  /** Page management */
  currentPage: number;
  setCurrentPage: (page: number) => void;

  /** Reset all filter state to defaults (without triggering onApply) */
  resetFilters: () => void;
}

export function useNFTFilters({
  defaultSort,
  syncURL = true,
  onApply,
}: UseNFTFiltersOptions): NFTFiltersState {
  const [searchParams, setSearchParams] = useSearchParams();

  const allSortOptions = [...pointsSortOptions, ...sortOptions];

  // Initialize state from URL params (if syncing)
  const initMinPrice = syncURL ? (searchParams.get('minPrice') || '') : '';
  const initMaxPrice = syncURL ? (searchParams.get('maxPrice') || '') : '';
  const initMinRarity = syncURL ? (searchParams.get('minRarity') || '') : '';
  const initMaxRarity = syncURL ? (searchParams.get('maxRarity') || '') : '';
  const initMinPoints = syncURL ? (searchParams.get('minPoints') || '') : '';
  const initMaxPoints = syncURL ? (searchParams.get('maxPoints') || '') : '';
  const initSearchQuery = syncURL ? (searchParams.get('search') || '') : '';
  const initSelectedTraits = (() => {
    if (!syncURL) return {};
    const traitsParam = searchParams.get('traits');
    if (traitsParam) {
      try {
        return JSON.parse(traitsParam);
      } catch (e) {
        console.error('Failed to parse traits from URL', e);
        return {};
      }
    }
    return {};
  })();

  const initOwnershipFilter = (() => {
    if (!syncURL) return 'all' as OwnershipFilter;
    const ownership = searchParams.get('ownership') as OwnershipFilter;
    return ownership && ownershipOptions.some(opt => opt.value === ownership) ? ownership : 'all';
  })();

  // State
  // `localSort` is only used when syncURL=false. `null` means "follow defaultSort",
  // which lets the sort track `defaultSort` as it updates (e.g., when the collection
  // loads and we learn it has a points system). When syncURL=true, sort is derived
  // directly from the URL param (see `sortOption` below) — no local state needed.
  const [localSort, setLocalSort] = useState<SortOption | null>(null);
  const [minPrice, setMinPrice] = useState(initMinPrice);
  const [maxPrice, setMaxPrice] = useState(initMaxPrice);
  const [minRarity, setMinRarity] = useState(initMinRarity);
  const [maxRarity, setMaxRarity] = useState(initMaxRarity);
  const [minPoints, setMinPoints] = useState(initMinPoints);
  const [maxPoints, setMaxPoints] = useState(initMaxPoints);
  const [searchQuery, setSearchQuery] = useState(initSearchQuery);
  const [selectedTraits, setSelectedTraits] = useState<{ [key: string]: string[] }>(initSelectedTraits);
  const [expandedTraits, setExpandedTraits] = useState<Set<string>>(new Set());
  const [ownershipFilter, setOwnershipFilter] = useState<OwnershipFilter>(initOwnershipFilter);
  const [showSortDropdown, setShowSortDropdown] = useState(false);
  const [showFilters, setShowFilters] = useState(false);
  const [showOwnershipDropdown, setShowOwnershipDropdown] = useState(false);
  const [currentPage, setCurrentPage] = useState(1);

  // Derived sort: URL is the source of truth when syncing, otherwise local state.
  // Falling back to `defaultSort` (rather than storing it in state) means the sort
  // tracks `defaultSort` synchronously — critical for collections where we don't
  // know the correct default (e.g. 'best-value' for points collections) until the
  // collection data loads. A useState+useEffect approach caused a race where the
  // initial NFT fetch went out with the stale default before the effect updated it.
  const sortOption: SortOption = useMemo(() => {
    if (syncURL) {
      const urlSort = searchParams.get('sort') as SortOption;
      if (urlSort && allSortOptions.some(opt => opt.value === urlSort)) return urlSort;
      return defaultSort;
    }
    return localSort ?? defaultSort;
  }, [syncURL, searchParams, defaultSort, localSort]);

  // Sync state from URL params when they change externally
  // (e.g., clicking a trait link on the traits tab navigates to NFTs tab with filter params).
  // Functional updaters ensure this is a no-op when the hook itself updated the URL.
  useEffect(() => {
    if (!syncURL) return;

    const urlTraits = searchParams.get('traits');
    let newTraits: Record<string, string[]> = {};
    if (urlTraits) {
      try { newTraits = JSON.parse(urlTraits); } catch (e) { /* ignore */ }
    }
    setSelectedTraits(prev =>
      JSON.stringify(prev) !== JSON.stringify(newTraits) ? newTraits : prev
    );

    const newMinPrice = searchParams.get('minPrice') || '';
    const newMaxPrice = searchParams.get('maxPrice') || '';
    const newMinRarity = searchParams.get('minRarity') || '';
    const newMaxRarity = searchParams.get('maxRarity') || '';
    const newMinPoints = searchParams.get('minPoints') || '';
    const newMaxPoints = searchParams.get('maxPoints') || '';
    const newSearch = searchParams.get('search') || '';
    setMinPrice(prev => prev !== newMinPrice ? newMinPrice : prev);
    setMaxPrice(prev => prev !== newMaxPrice ? newMaxPrice : prev);
    setMinRarity(prev => prev !== newMinRarity ? newMinRarity : prev);
    setMaxRarity(prev => prev !== newMaxRarity ? newMaxRarity : prev);
    setMinPoints(prev => prev !== newMinPoints ? newMinPoints : prev);
    setMaxPoints(prev => prev !== newMaxPoints ? newMaxPoints : prev);
    setSearchQuery(prev => prev !== newSearch ? newSearch : prev);

    const urlOwnership = searchParams.get('ownership') as OwnershipFilter;
    const newOwnership = urlOwnership && ownershipOptions.some(opt => opt.value === urlOwnership) ? urlOwnership : 'all';
    setOwnershipFilter(prev => prev !== newOwnership ? newOwnership : prev);
  }, [searchParams, syncURL]);

  const updateURLParams = useCallback(() => {
    if (!syncURL) return;
    const params = new URLSearchParams();

    // Preserve non-filter params (like tab)
    const tab = searchParams.get('tab');
    if (tab) params.set('tab', tab);

    if (sortOption !== defaultSort) params.set('sort', sortOption);
    if (minPrice) params.set('minPrice', minPrice);
    if (maxPrice) params.set('maxPrice', maxPrice);
    if (minRarity) params.set('minRarity', minRarity);
    if (maxRarity) params.set('maxRarity', maxRarity);
    if (minPoints) params.set('minPoints', minPoints);
    if (maxPoints) params.set('maxPoints', maxPoints);
    if (searchQuery) params.set('search', searchQuery);
    if (Object.keys(selectedTraits).length > 0) {
      params.set('traits', JSON.stringify(selectedTraits));
    }
    if (ownershipFilter !== 'all') params.set('ownership', ownershipFilter);

    setSearchParams(params);
  }, [syncURL, searchParams, setSearchParams, defaultSort, sortOption, minPrice, maxPrice, minRarity, maxRarity, minPoints, maxPoints, searchQuery, selectedTraits, ownershipFilter]);

  const handleSortChange = useCallback((newSort: SortOption) => {
    setShowSortDropdown(false);
    setCurrentPage(1);

    if (syncURL) {
      const params = new URLSearchParams(searchParams);
      if (newSort !== defaultSort) {
        params.set('sort', newSort);
      } else {
        params.delete('sort');
      }
      setSearchParams(params);
    } else {
      // Store null when it matches the default so the sort can still track
      // future defaultSort changes (e.g., collection data finishing loading).
      setLocalSort(newSort === defaultSort ? null : newSort);
    }
  }, [syncURL, searchParams, setSearchParams, defaultSort]);

  const handleFilterApply = useCallback(() => {
    setCurrentPage(1);
    updateURLParams();
    setShowFilters(false);
    onApply?.();
  }, [updateURLParams, onApply]);

  const handleFilterClear = useCallback(() => {
    setMinPrice('');
    setMaxPrice('');
    setMinRarity('');
    setMaxRarity('');
    setMinPoints('');
    setMaxPoints('');
    setSearchQuery('');
    setSelectedTraits({});
    setOwnershipFilter('all');
    setCurrentPage(1);

    if (syncURL) {
      const params = new URLSearchParams();
      const tab = searchParams.get('tab');
      if (tab) params.set('tab', tab);
      if (sortOption !== defaultSort) params.set('sort', sortOption);
      setSearchParams(params);
    }

    // Defer onApply to let state updates flush
    setTimeout(() => onApply?.(), 0);
  }, [syncURL, searchParams, setSearchParams, defaultSort, sortOption, onApply]);

  const handleClearPriceFilter = useCallback(() => {
    setMinPrice('');
    setMaxPrice('');
    setCurrentPage(1);

    if (syncURL) {
      const params = new URLSearchParams();
      const tab = searchParams.get('tab');
      if (tab) params.set('tab', tab);
      if (sortOption !== defaultSort) params.set('sort', sortOption);
      if (minRarity) params.set('minRarity', minRarity);
      if (maxRarity) params.set('maxRarity', maxRarity);
      if (minPoints) params.set('minPoints', minPoints);
      if (maxPoints) params.set('maxPoints', maxPoints);
      if (searchQuery) params.set('search', searchQuery);
      if (Object.keys(selectedTraits).length > 0) {
        params.set('traits', JSON.stringify(selectedTraits));
      }
      if (ownershipFilter !== 'all') params.set('ownership', ownershipFilter);
      setSearchParams(params);
    }

    onApply?.();
  }, [syncURL, searchParams, setSearchParams, defaultSort, sortOption, minRarity, maxRarity, minPoints, maxPoints, searchQuery, selectedTraits, ownershipFilter, onApply]);

  const handleClearRarityFilter = useCallback(() => {
    setMinRarity('');
    setMaxRarity('');
    setCurrentPage(1);

    if (syncURL) {
      const params = new URLSearchParams();
      const tab = searchParams.get('tab');
      if (tab) params.set('tab', tab);
      if (sortOption !== defaultSort) params.set('sort', sortOption);
      if (minPrice) params.set('minPrice', minPrice);
      if (maxPrice) params.set('maxPrice', maxPrice);
      if (minPoints) params.set('minPoints', minPoints);
      if (maxPoints) params.set('maxPoints', maxPoints);
      if (searchQuery) params.set('search', searchQuery);
      if (Object.keys(selectedTraits).length > 0) {
        params.set('traits', JSON.stringify(selectedTraits));
      }
      if (ownershipFilter !== 'all') params.set('ownership', ownershipFilter);
      setSearchParams(params);
    }

    onApply?.();
  }, [syncURL, searchParams, setSearchParams, defaultSort, sortOption, minPrice, maxPrice, minPoints, maxPoints, searchQuery, selectedTraits, ownershipFilter, onApply]);

  const handleClearPointsFilter = useCallback(() => {
    setMinPoints('');
    setMaxPoints('');
    setCurrentPage(1);

    if (syncURL) {
      const params = new URLSearchParams();
      const tab = searchParams.get('tab');
      if (tab) params.set('tab', tab);
      if (sortOption !== defaultSort) params.set('sort', sortOption);
      if (minPrice) params.set('minPrice', minPrice);
      if (maxPrice) params.set('maxPrice', maxPrice);
      if (minRarity) params.set('minRarity', minRarity);
      if (maxRarity) params.set('maxRarity', maxRarity);
      if (searchQuery) params.set('search', searchQuery);
      if (Object.keys(selectedTraits).length > 0) {
        params.set('traits', JSON.stringify(selectedTraits));
      }
      if (ownershipFilter !== 'all') params.set('ownership', ownershipFilter);
      setSearchParams(params);
    }

    onApply?.();
  }, [syncURL, searchParams, setSearchParams, defaultSort, sortOption, minPrice, maxPrice, minRarity, maxRarity, searchQuery, selectedTraits, ownershipFilter, onApply]);

  const handleTraitToggle = useCallback((traitType: string, value: string) => {
    setSelectedTraits(prev => {
      const current = prev[traitType] || [];
      const isSelected = current.includes(value);

      let newTraits: Record<string, string[]>;
      if (isSelected) {
        const updated = current.filter(v => v !== value);
        if (updated.length === 0) {
          const { [traitType]: _, ...rest } = prev;
          newTraits = rest;
        } else {
          newTraits = { ...prev, [traitType]: updated };
        }
      } else {
        newTraits = { ...prev, [traitType]: [...current, value] };
      }

      if (syncURL) {
        const params = new URLSearchParams(searchParams);
        if (Object.keys(newTraits).length > 0) {
          params.set('traits', JSON.stringify(newTraits));
        } else {
          params.delete('traits');
        }
        setSearchParams(params);
      }

      return newTraits;
    });
    setCurrentPage(1);
  }, [syncURL, searchParams, setSearchParams]);

  const toggleTraitExpanded = useCallback((traitType: string) => {
    setExpandedTraits(prev => {
      const newSet = new Set(prev);
      if (newSet.has(traitType)) {
        newSet.delete(traitType);
      } else {
        newSet.add(traitType);
      }
      return newSet;
    });
  }, []);

  const getCurrentSortLabel = useCallback((): string => {
    const allOptions = [...pointsSortOptions, ...sortOptions];
    return allOptions.find(opt => opt.value === sortOption)?.label || 'Recently Listed';
  }, [sortOption]);

  const buildApiParams = useCallback((): URLSearchParams => {
    const params = new URLSearchParams();

    const [sortBy, sortOrder] = parseSortOption(sortOption);
    params.set('sortBy', sortBy);
    params.set('sortOrder', sortOrder);

    if (minPrice) params.set('minPrice', minPrice);
    if (maxPrice) params.set('maxPrice', maxPrice);
    if (minRarity) params.set('minRank', minRarity);
    if (maxRarity) params.set('maxRank', maxRarity);
    if (minPoints) params.set('minPoints', minPoints);
    if (maxPoints) params.set('maxPoints', maxPoints);
    if (searchQuery) params.set('search', searchQuery);
    if (Object.keys(selectedTraits).length > 0) {
      params.set('traits', JSON.stringify(selectedTraits));
    }

    return params;
  }, [sortOption, minPrice, maxPrice, minRarity, maxRarity, minPoints, maxPoints, searchQuery, selectedTraits]);

  const resetFilters = useCallback(() => {
    setLocalSort(null);
    setMinPrice('');
    setMaxPrice('');
    setMinRarity('');
    setMaxRarity('');
    setMinPoints('');
    setMaxPoints('');
    setSearchQuery('');
    setSelectedTraits({});
    setOwnershipFilter('all');
    setExpandedTraits(new Set());
    setCurrentPage(1);
  }, []);

  const hasActiveFilters = Object.keys(selectedTraits).length > 0 ||
    !!minPrice || !!maxPrice || !!minRarity || !!maxRarity || !!minPoints || !!maxPoints;

  return {
    sortOption,
    minPrice,
    maxPrice,
    minRarity,
    maxRarity,
    minPoints,
    maxPoints,
    searchQuery,
    selectedTraits,
    expandedTraits,
    ownershipFilter,
    showSortDropdown,
    showFilters,
    showOwnershipDropdown,
    setMinPrice,
    setMaxPrice,
    setMinRarity,
    setMaxRarity,
    setMinPoints,
    setMaxPoints,
    setSearchQuery,
    setOwnershipFilter,
    setShowSortDropdown,
    setShowFilters,
    setShowOwnershipDropdown,
    handleSortChange,
    handleFilterApply,
    handleFilterClear,
    handleClearPriceFilter,
    handleClearRarityFilter,
    handleClearPointsFilter,
    handleTraitToggle,
    toggleTraitExpanded,
    getCurrentSortLabel,
    buildApiParams,
    hasActiveFilters,
    currentPage,
    setCurrentPage,
    resetFilters,
  };
}
